import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authenticatedFetch } from '../services/authService';

interface AutocompleteOption {
  nazwa: string;
  nip: string;
  telefon: string;
  miasto: string;
  budowa?: string;
  klasa?: string;
  konsystencja?: string;
  frequency?: number;
  last_used?: string;
  is_confirmed?: boolean | null; // true = confirmed, false = unconfirmed, null = no client/NIP
  // Client-specific fields
  zadluzenie?: number;
  limit_kredytowy?: number;
  przeterminowane?: number;
  niezafakturowane?: number;
  blokada?: boolean;
  zgoda?: boolean;
}

interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlurInput?: (value: string) => void;
  onClientSelect?: (client: AutocompleteOption) => void;
  onSelectionComplete?: () => void;
  placeholder?: string;
  fieldType: 'nazwa' | 'nip' | 'budowa' | 'telefon' | 'pompa_nazwa' | 'pompa_numer' | 'klasa';
  style?: React.CSSProperties;
  className?: string;
  dataField?: string;
  dataRowId?: string | number;
  clientName?: string;
  clientNip?: string;
  regionId?: number;
  wezelId?: number;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  onKeyPress,
  onKeyDown,
  onClientSelect,
  onSelectionComplete,
  onBlurInput,
  placeholder,
  fieldType,
  style,
  className,
  dataField,
  dataRowId,
  clientName,
  clientNip,
  regionId,
  wezelId
}) => {
  const { isAdmin, hasRole } = useAuth();
  const canSeeFinancial = isAdmin() || hasRole('Księgowość') || hasRole('Ksiegowosc');
  const [suggestions, setSuggestions] = useState<AutocompleteOption[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const selectionJustMadeRef = useRef<boolean>(false);
  const blurTimeoutRef = useRef<number | null>(null);

  // OPTIMIZATION: Debounced autocomplete to prevent excessive API calls (429 errors)
  useEffect(() => {
    const fetchSuggestions = async () => {
      // Skip one fetch cycle right after selecting a suggestion to avoid reopening the list
      if (selectionJustMadeRef.current) {
        selectionJustMadeRef.current = false;
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      // OPTIMIZATION: Don't fetch suggestions for empty values to reduce API calls
      if (fieldType === 'budowa' || fieldType === 'telefon') {
        // For budowa and telefon, we need at least client name or NIP
        // Value can be empty - we'll show all suggestions for this client
        if (!clientName && !clientNip) {
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }
      } else if (fieldType === 'klasa') {
        // For klasa: if client has NIP, show immediately (even empty), otherwise wait for 1 char
        if (clientName && clientNip) {
          // Client with NIP - allow empty value to show confirmed recepts
          // No additional check needed
        } else {
          // Client without NIP - need at least 1 character
          if (!value || value.length < 1) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
          }
        }
      } else if (fieldType === 'pompa_nazwa' || fieldType === 'pompa_numer') {
        // For pumps: allow 1+ char to start searching
        if (!value || value.length < 1) {
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }
      } else {
        // For nazwa and nip, need at least 2 characters
        if (!value || value.length < 2) {
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }
      }
      
      // Additional optimization: skip fetch if value is just whitespace (but not for budowa, telefon, or klasa with client)
      if (fieldType !== 'budowa' && fieldType !== 'telefon' && 
          !(fieldType === 'klasa' && clientName && clientNip) && 
          value && value.trim().length === 0) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        setLoading(true);

        let url = '';
        if (fieldType === 'budowa' || fieldType === 'telefon') {
          // For budowa and telefon, build URL with client params first, then optional q
          const params = [];
          if (clientName) {
            params.push(`nazwa=${encodeURIComponent(clientName)}`);
          }
          if (clientNip) {
            params.push(`nip=${encodeURIComponent(clientNip)}`);
          }
          // Add q parameter only if value is not empty
          if (value && value.trim().length > 0) {
            params.push(`q=${encodeURIComponent(value)}`);
          }
          url = `/api/klient/autocomplete/${fieldType}?${params.join('&')}`;
        } else if (fieldType === 'pompa_nazwa') {
          url = `/api/pompa/autocomplete/nazwa?q=${encodeURIComponent(value)}${regionId ? `&region_id=${regionId}` : ''}`;
        } else if (fieldType === 'pompa_numer') {
          url = `/api/pompa/autocomplete/nrpompy?q=${encodeURIComponent(value)}${regionId ? `&region_id=${regionId}` : ''}`;
        } else if (fieldType === 'klasa') {
          // regionId is required for klasa
          if (!regionId) {
            setSuggestions([]);
            setShowSuggestions(false);
            setLoading(false);
            return;
          }
          const qp = [
            `region_id=${regionId}`,
            (value && value.trim().length > 0) ? `q=${encodeURIComponent(value)}` : null,
            clientName ? `nazwa=${encodeURIComponent(clientName)}` : null,
            clientNip ? `nip=${encodeURIComponent(clientNip)}` : null,
            wezelId ? `wezel_id=${encodeURIComponent(String(wezelId))}` : null
          ].filter(Boolean).join('&');
          url = `/api/klient/autocomplete/klasa?${qp}`;
        } else {
          url = `/api/klient/autocomplete/${fieldType}?q=${encodeURIComponent(value)}`;
        }

        const response = await authenticatedFetch(url, { credentials: 'include' });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const limited = (data.results || []).slice(0, 20);
            setSuggestions(limited);
            // Show suggestions if we have results and input is focused
            // For budowa/telefon/klasa with client, show even if value is empty
            const shouldShow = limited.length > 0 && isFocused;
            if (shouldShow) {
              setShowSuggestions(true);
            }
            setSelectedIndex(-1);
          }
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300); // OPTIMIZATION: Debounce to reduce API calls and prevent 429 errors
    return () => clearTimeout(timeoutId);
  }, [value, fieldType, clientName, clientNip, wezelId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is on another autocomplete input - if so, don't close this one immediately
      const isAnotherAutocomplete = (target as HTMLElement)?.closest('.autocomplete-input') ||
                                   (target as HTMLElement)?.tagName === 'INPUT' ||
                                   (target as HTMLElement)?.hasAttribute('data-field');
      
      if (
        inputRef.current && 
        !inputRef.current.contains(target) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(target)
      ) {
        // If clicking on another autocomplete, delay closing slightly to allow focus to move
        if (isAnotherAutocomplete) {
          setTimeout(() => {
            // Only close if focus didn't move to this input
            if (document.activeElement !== inputRef.current) {
              setShowSuggestions(false);
              setSuggestions([]);
            }
          }, 100);
        } else {
          setShowSuggestions(false);
          setSuggestions([]);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (!selectionJustMadeRef.current) {
      setShowSuggestions(true);
    }
  };

  // Function to check if client is blocked
  const isClientBlocked = (suggestion: AutocompleteOption): boolean => {
    if (fieldType !== 'nazwa' && fieldType !== 'nip') return false;
    
    // Only check financial status for clients with valid NIP
    if (!suggestion.nip || suggestion.nip.trim() === '') {
      return false;
    }
    
    const zadluzenie = suggestion.zadluzenie || 0;
    const limitKredytowy = suggestion.limit_kredytowy || 0;
    const przeterminowane = suggestion.przeterminowane || 0;
    const blokada = suggestion.blokada || false;
    const zgoda = suggestion.zgoda || false;
    
    // Debug logging for blocked clients
    if (blokada === true) {
      console.log('🔴 Frontend: Found blocked client:', {
        nazwa: suggestion.nazwa,
        nip: suggestion.nip,
        blokada,
        zgoda,
        zadluzenie,
        limitKredytowy,
        przeterminowane
      });
    }
    
    // Client is blocked if: (zadluzenie > limit_kredytowy) OR przeterminowane OR blokada) AND NOT zgoda
    return ((zadluzenie > limitKredytowy) || przeterminowane > 0 || blokada) && !zgoda;
  };

  // Function to get client status color
  const getClientStatusColor = (suggestion: AutocompleteOption): string => {
    if (fieldType !== 'nazwa' && fieldType !== 'nip') return 'var(--text)';
    
    // Only apply financial status colors for clients with valid NIP
    if (!suggestion.nip || suggestion.nip.trim() === '') {
      return 'var(--text)'; // Default color for clients without NIP
    }
    
    if (suggestion.zgoda) return 'var(--ok)'; // Green for approved clients
    if (isClientBlocked(suggestion)) return 'var(--error)'; // Red for blocked clients
    
    // Debug logging for color selection
    if (suggestion.blokada === true) {
      console.log('🎨 Frontend: Color for blocked client:', {
        nazwa: suggestion.nazwa,
        nip: suggestion.nip,
        blokada: suggestion.blokada,
        zgoda: suggestion.zgoda,
        isBlocked: isClientBlocked(suggestion),
        color: isClientBlocked(suggestion) ? 'var(--error)' : 'var(--text)'
      });
    }
    
    return 'var(--text)'; // Default color
  };

  // Helper function to format financial data for confirmation message
  const formatFinancialData = (suggestion: AutocompleteOption): string => {
    const zadluzenie = Number(suggestion.zadluzenie) || 0;
    const limitKredytowy = Number(suggestion.limit_kredytowy) || 0;
    const przeterminowane = Number(suggestion.przeterminowane) || 0;
    const niezafakturowane = Number(suggestion.niezafakturowane) || 0;
    const razem = zadluzenie + niezafakturowane;
    
    return `Zadłużenie: ${zadluzenie.toFixed(2)} zł\n` +
           `Niezafakturowane: ${niezafakturowane.toFixed(2)} zł\n` +
           `Razem: ${razem.toFixed(2)} zł\n` +
           `Przeterminowane: ${przeterminowane.toFixed(2)} zł\n` +
           `Limit: ${limitKredytowy.toFixed(2)} zł`;
  };

  const handleSuggestionClick = (suggestion: AutocompleteOption) => {
    // Check if client is blocked and show warning
    if ((fieldType === 'nazwa' || fieldType === 'nip') && isClientBlocked(suggestion)) {
      let message = '⚠️ OSTRZEŻENIE!\n\n' +
        'Składanie zamówienia na klienta zabronione!\n\n' +
        `Klient: ${suggestion.nazwa}\n` +
        `NIP: ${suggestion.nip}\n\n`;
      
      // Add financial data only for users with appropriate roles
      if (canSeeFinancial) {
        message += 'Dane finansowe:\n' + formatFinancialData(suggestion) + '\n\n';
      }
      
      message += 'Czy na pewno chcesz wybrać tego klienta?';
      
      const confirmSelection = window.confirm(message);
      
      if (!confirmSelection) {
        return; // Don't proceed with selection
      }
    }

    selectionJustMadeRef.current = true;
      if (fieldType === 'nazwa') {
      onChange(suggestion.nazwa);
    } else if (fieldType === 'nip') {
      onChange(suggestion.nip);
    } else if (fieldType === 'budowa') {
      onChange(suggestion.budowa || '');
    } else if (fieldType === 'telefon') {
      onChange(suggestion.telefon || '');
      } else if (fieldType === 'pompa_nazwa') {
        onChange((suggestion as any).nazwa || '');
      } else if (fieldType === 'pompa_numer') {
        onChange((suggestion as any).nrpompy || '');
      } else if (fieldType === 'klasa') {
        onChange((suggestion as any).nazwa || '');
      }
    
    // Notify parent component about the selected client
    if (onClientSelect) {
      onClientSelect(suggestion);
    }
    
    // Hide immediately on selection
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    
    // Call selection complete callback after a short delay to allow state updates
    setTimeout(() => {
      if (onSelectionComplete) {
        onSelectionComplete();
      }
    }, 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      // Forward only one callback to avoid double-calling parent handlers
      if (onKeyDown) {
        onKeyDown(e);
      } else {
        onKeyPress(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        // Only select from autocomplete if a suggestion is explicitly highlighted
        const chosenIndex = selectedIndex;
        if (chosenIndex >= 0 && suggestions[chosenIndex]) {
          const selectedSuggestion = suggestions[chosenIndex];
          
          // Check if client is blocked and show warning
          if ((fieldType === 'nazwa' || fieldType === 'nip') && isClientBlocked(selectedSuggestion)) {
            let message = '⚠️ OSTRZEŻENIE!\n\n' +
              'Składanie zamówienia na klienta zabronione!\n\n' +
              `Klient: ${selectedSuggestion.nazwa}\n` +
              `NIP: ${selectedSuggestion.nip}\n\n`;
            
            // Add financial data only for users with appropriate roles
            if (canSeeFinancial) {
              message += 'Dane finansowe:\n' + formatFinancialData(selectedSuggestion) + '\n\n';
            }
            
            message += 'Czy na pewno chcesz wybrać tego klienta?';
            
            const confirmSelection = window.confirm(message);
            
            if (!confirmSelection) {
              return; // Don't proceed with selection
            }
          }
          
          selectionJustMadeRef.current = true;
          if (fieldType === 'nazwa') {
            onChange(selectedSuggestion.nazwa);
          } else if (fieldType === 'nip') {
            onChange(selectedSuggestion.nip);
          } else if (fieldType === 'budowa') {
            onChange(selectedSuggestion.budowa || '');
          } else if (fieldType === 'telefon') {
            onChange(selectedSuggestion.telefon || '');
          } else if (fieldType === 'pompa_nazwa') {
            onChange((selectedSuggestion as any).nazwa || '');
          } else if (fieldType === 'pompa_numer') {
            onChange((selectedSuggestion as any).nrpompy || '');
          } else if (fieldType === 'klasa') {
            onChange((selectedSuggestion as any).nazwa || '');
          }

          if (onClientSelect) {
            onClientSelect(selectedSuggestion);
          }

          setShowSuggestions(false);
          setSuggestions([]);
          setSelectedIndex(-1);

          setTimeout(() => {
            if (onSelectionComplete) {
              onSelectionComplete();
            }
          }, 50);
        } else {
          // No highlighted suggestion: close list and forward Enter to parent (e.g., save)
          setShowSuggestions(false);
          setSelectedIndex(-1);
          if (onKeyDown) {
            onKeyDown(e);
          } else {
            onKeyPress(e);
          }
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        if (onKeyDown) {
          onKeyDown(e);
        } else {
          onKeyPress(e);
        }
        break;
      case 'Tab':
        // Close suggestions when tabbing away
        setShowSuggestions(false);
        setSelectedIndex(-1);
        setSuggestions([]);
        if (onKeyDown) onKeyDown(e);
        break;
      default:
        if (onKeyDown) {
          onKeyDown(e);
        } else {
          onKeyPress(e);
        }
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className={className}
        onFocus={() => {
          // Cancel any pending blur timeout
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
          }
          
          setIsFocused(true);
          // For budowa, telefon, and klasa fields, if client name/NIP is filled, trigger fetch on focus
          // This allows showing suggestions immediately when user focuses the field
          if ((fieldType === 'budowa' || fieldType === 'telefon' || 
               (fieldType === 'klasa' && clientName && clientNip)) && (clientName || clientNip)) {
            // Trigger fetch by showing suggestions if we have any, or wait for fetch to complete
            // The useEffect will fetch and then show suggestions
            setShowSuggestions(suggestions.length > 0);
          } else {
            setShowSuggestions(suggestions.length > 0);
          }
        }}
        onBlur={(e) => {
          setIsFocused(false);
          // Clear any pending blur timeout
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
          }
          
          // Check if focus is moving to another autocomplete input (relatedTarget)
          const relatedTarget = e.relatedTarget as HTMLElement | null;
          const isMovingToAutocomplete = relatedTarget && (
            relatedTarget.tagName === 'INPUT' ||
            relatedTarget.closest('.autocomplete-input') ||
            relatedTarget.closest('[data-field]')
          );
          
          // Close suggestions when leaving the field, but with delay to allow focus to move to another autocomplete
          // Use longer timeout if moving to another autocomplete input to prevent premature closing
          const timeout = isMovingToAutocomplete ? 200 : 150;
          blurTimeoutRef.current = window.setTimeout(() => {
            // Double-check: if input is now focused again, don't close
            if (document.activeElement !== inputRef.current) {
              setShowSuggestions(false);
              setSuggestions([]);
              setSelectedIndex(-1);
            }
            blurTimeoutRef.current = null;
          }, timeout);
          try { if (onBlurInput) onBlurInput(value); } catch {}
        }}
        placeholder={placeholder}
        data-field={dataField}
        data-row-id={dataRowId}
        style={{
          ...style,
          width: '100%'
        }}
      />
      
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
            minWidth: '280px',
            maxWidth: '500px',
            width: 'max-content'
          }}
        >
          {loading ? (
            <div style={{ padding: '8px 12px', color: 'var(--muted)', fontSize: '11px' }}>
              Wyszukiwanie...
            </div>
          ) : suggestions.length === 0 ? (
            <div style={{ padding: '8px 12px', color: 'var(--muted)', fontSize: '11px' }}>
              Brak wyników
            </div>
          ) : (
            suggestions.map((suggestion, index) => {
              const isKlasa = fieldType === 'klasa';
              const isClient = fieldType === 'nazwa' || fieldType === 'nip';
              return (
                <div
                  key={`${suggestion.nazwa}-${suggestion.nip}-${index}`}
                  onMouseDown={(ev) => { ev.preventDefault(); handleSuggestionClick(suggestion); }}
                  style={{
                    padding: (isKlasa || isClient || fieldType === 'budowa' || fieldType === 'telefon') ? '6px 10px' : '10px 16px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    backgroundColor: index === selectedIndex 
                      ? 'rgba(59, 130, 246, 0.15)' // lighter blue background for better readability with colored text
                      : (isKlasa && suggestion.is_confirmed === true 
                         ? 'rgba(22, 163, 74, 0.1)' // light green background for confirmed
                         : 'transparent'),
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '0',
                    lineHeight: '1.2'
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {/* Single-line compact layout */}
                  <span style={{ 
                    fontWeight: 600, 
                    fontSize: isKlasa ? 12 : 13, 
                    color: isKlasa && suggestion.is_confirmed !== null 
                      ? (suggestion.is_confirmed === true ? 'var(--ok)' : 'var(--error)')
                      : getClientStatusColor(suggestion)
                  }}>
                    {fieldType === 'nazwa' ? suggestion.nazwa :
                     fieldType === 'nip' ? suggestion.nip :
                     fieldType === 'pompa_nazwa' ? (suggestion as any).nazwa :
                     fieldType === 'pompa_numer' ? (suggestion as any).nrpompy :
                     fieldType === 'klasa' ? (suggestion as any).nazwa :
                     fieldType === 'telefon' ? (suggestion.telefon || '') :
                     suggestion.budowa}
                  </span>
                  {isClient && (
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{suggestion.nip ? `NIP: ${suggestion.nip}` : ''}</span>
                  )}
                  {fieldType === 'budowa' && (
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{suggestion.nazwa}</span>
                  )}
                  {fieldType === 'telefon' && (
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{suggestion.nazwa}</span>
                  )}
                  {fieldType === 'pompa_nazwa' && (suggestion as any).kierowca && (
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>({(suggestion as any).kierowca})</span>
                  )}
                  {fieldType === 'pompa_numer' && (
                    <>
                      {(suggestion as any).nazwa && (
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{(suggestion as any).nazwa}</span>
                      )}
                      {(suggestion as any).kierowca && (
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>({(suggestion as any).kierowca})</span>
                      )}
                    </>
                  )}
                  {(isClient && canSeeFinancial) && (
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8, fontSize: 11 }}>
                      {suggestion.zgoda && <span style={{ color: 'var(--ok)', fontWeight: 600 }}>✅ ZGODA</span>}
                      {(((suggestion.zadluzenie || 0) > (suggestion.limit_kredytowy || 0)) || ((suggestion.przeterminowane || 0) > 0) || suggestion.blokada) && (
                        <span style={{ color: 'var(--error)', fontWeight: 600 }}>🚫 BLOKADA</span>
                      )}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput; 